import {
   ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ResizedEvent } from 'angular-resize-event';
import { MatDialog } from '@angular/material/dialog';
import { assign, cloneDeep } from 'lodash';
import { Select } from '@ngxs/store';
import { Observable, map, filter, take, Subject, takeUntil, combineLatest, BehaviorSubject } from 'rxjs';

import { ServerLinks } from '@shared/services/server-links';
import { ServerLinkName } from '@shared/models/server-links-model';
import { CustomIcons } from '@shared/models/custom-icons';
import { convertCatalogItemFieldsVMToView } from '@utils/converters';
import { CatalogItemValue } from 'app/modules/data/models/catalog';
import { CatalogDynamicColumns } from 'app/modules/data/modules/catalog-items-data/models/catalog-item-component';
import { CatalogItemsState } from 'app/modules/data/modules/catalog-items-data/store/catalog-items-state';
import { CatalogItemFieldsVM, CatalogType } from '@shared/models/shared-catalog.state';
import { FileData, SelectOptionMobile } from '@shared/models/catalog.service';
import { CatalogItemCardLinkModalComponent } from '../catalog-item-card-link-modal/catalog-item-card-link-modal.component';
import { CatalogItemsModalComponent } from '../catalog-items-modal/catalog-items-modal.component';
import { CatalogItemTableData } from 'app/modules/data/shared/models/catalog-item-table-data';

@Component({
  selector: 'app-catalog-item-card-info',
  templateUrl: './catalog-item-card-info.component.html',
  styleUrls: ['./catalog-item-card-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CatalogItemCardInfoComponent implements OnInit, OnDestroy {
  private _mediaPath = '';
  private _allTableItem: CatalogItemTableData[] = [];
  private _itemData: CatalogItemValue;
  private _itemColumns: CatalogDynamicColumns[];
  public downloadLinkPrefix = this.serverLinks.getLink(ServerLinkName.DownloadFile);
  public filePath = '';
  public tableType = CatalogType.Catalog;
  public linkType = CatalogType.LINK;
  public dateType = CatalogType.Date;
  public dictionaryType = CatalogType.Dictionary;
  public fileType = CatalogType.File;
  public icons = CustomIcons;
  public itemHeight = 'calc(100% - var(--title-top-padding))';
  public emptyValue: CatalogItemTableData[] = [];
  public columnsMap = new Map();
  public rightColTitle = '';
  itemDataLoaded$ = new BehaviorSubject<CatalogItemValue | null>(null);
  itemColumnsLoaded$ = new BehaviorSubject<CatalogItemFieldsVM[]>([]);
  dataLoaded$ = combineLatest([this.itemDataLoaded$, this.itemColumnsLoaded$]);

  public get itemData(): CatalogItemValue | null {
    return this._itemData;
  }

  @Input()
  public set itemData(value: CatalogItemValue) {
    if (value && value !== this._itemData) {
      this.emptyValue = [];
      this._allTableItem = [];
      this.itemDataLoaded$.next(value);
    }
  }

  public get itemColumns(): CatalogDynamicColumns[] {
    return this._itemColumns;
  }

  @Input() public showAllFields = true;
  @Input() public isImpaired = false;
  @Input() public showImpairedTitle = false;

  @Input()
  public set itemFields(fields: CatalogItemFieldsVM[] | null) {
    if (fields) {
      const dynamicFields = convertCatalogItemFieldsVMToView(fields).sort((a, b) => (a.order > b.order ? 1 : -1));
      let titleIndex = dynamicFields.findIndex(x => x.systemField?.includes('name'));
      if (titleIndex === -1) {
        const findOrderIndex = dynamicFields.findIndex(x => x.systemField?.includes('name') || x.show);
        titleIndex = findOrderIndex === -1 ? 0 : findOrderIndex;
      }
      this.rightColTitle = dynamicFields[titleIndex]?.name;
      this.itemColumnsLoaded$.next(fields);
    }
  }

  @Input() public borderRadius = '0px';
  @Input() public showFloatTitle = false;
  public fileNameMap = new Map<string, FileData>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() public readonly close = new EventEmitter<void>();
  @Input() public headTitle = '';
  @Input() public title = '';

  @Select(CatalogItemsState.getFileData) public fileData$: Observable<FileData[]>;

  private componentDestroy = new Subject<void>;

  get showEmptyValueButton(): boolean {
    return this.hasEmptyFields(this.itemData || {});
  }

  constructor(
    private serverLinks: ServerLinks,
    @Inject(DOCUMENT) private document: Document,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
  ) {
    this.filePath = serverLinks.getLink(ServerLinkName.File);
    this._mediaPath = serverLinks.getLink(ServerLinkName.Media);
  }

  ngOnInit(): void {
    this.dataLoaded$
      .pipe(
        filter(([data, columns]) => !!data && !!columns.length),
        takeUntil(this.componentDestroy)
      )
      .subscribe(([data, columns]) => {
        const dynamicFields = convertCatalogItemFieldsVMToView(columns).sort((a, b) => (a.order > b.order ? 1 : -1));
        this._itemColumns = this.setImageValue(dynamicFields, data);
        this._itemColumns = this.setImageValue(this._itemColumns, data);
        this.itemColumns.forEach(elem => {
          this.columnsMap.set(elem.name, elem.type);
        });
        const newObjNotEn: CatalogItemValue = {};
        Object.keys(data)
          .forEach(key => { newObjNotEn[key] = cloneDeep(data[key]); });
        this.downObject(newObjNotEn);
        this._itemData = newObjNotEn;
      });

    this.fileData$
      .pipe(takeUntil(this.componentDestroy))
      .subscribe((fileData: FileData[]) => {
        fileData.forEach((elem: FileData) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          this.fileNameMap.set(elem.id, elem);
        });
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.componentDestroy.next();
    this.componentDestroy.complete();
  }

  private downObject(obj: CatalogItemValue, path: string[] = []): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    Object.keys(obj).forEach(key => {
      if (Array.isArray(obj[key]) && this.columnsMap.get(key) !== 'LINK') {
        this._allTableItem.push(obj[key]);
        path.push(key);
        if (obj[key][0] && typeof obj[key][0] === 'object') {
          (obj[key] as CatalogItemValue[]).forEach(v => {
            this.downObject(v, path);
          });
          path.pop();
        } else {
          obj[key] = this.addShowValue(key, obj[key], path);
          path.pop();
        }
      } else {
        obj[key] = this.addShowValue(key, obj[key], path);
      }
    });
  }

  private isImageTypeFile(id: string): Observable<{
    isImage: boolean;
    isSvg: boolean;
  }> {
    return this.http.head(`${this._mediaPath}showFile?id=${id}`, { observe: 'response', responseType: 'blob' }).pipe(
      map(res => {
        if (res.headers.get('content-type')?.includes('image')) {
          let isSvg = false;
          const lengthHeader = res.headers.get('content-length');
          if (lengthHeader && +lengthHeader < 10000) {
            isSvg = true;
          }

          return {
            isImage: true,
            isSvg: isSvg
          };
        }

        return {
          isImage: false,
          isSvg: false
        };
      }),
      take(1)
    );
  }

  private findColumn = (key: string, path: string[], itemColumns: CatalogDynamicColumns[]): CatalogDynamicColumns | null => {
    let newItemColumns = itemColumns;

    let index = 0;
    while (path.length > index) {
      const p = path[index];
      newItemColumns = newItemColumns.find(x => x.name === p)?.catalogColumns ?? [];
      index += 1;
    }

    for (let i = 0; i < newItemColumns?.sort().length; i++) {
      if (newItemColumns[i].name === key) {
        return newItemColumns[i];
      }
    }

    return null;
  };

  private addShowValue = (key: string, value: CatalogItemTableData, path: string[]): CatalogItemTableData => {

    if (Array.isArray(value) && this.columnsMap.get(key) !== 'LINK') {
      if (value.length === 0) {
        const newValue = assign(value, { show: false, value: ' ' });
        this.emptyValue.push(newValue);
        return newValue;
      }
      return value;
    }

    if (Array.isArray(value) && this.columnsMap.get(key) === 'LINK') {
      if (value.length === 0) {
        const newValue = assign(value, { show: false, value: [] });
        this.emptyValue.push(newValue);
        return newValue;
      }
    }

    if (this.itemColumns) {
      const currentColumn = this.findColumn(key, path, this.itemColumns);
      if (currentColumn && currentColumn.type === this.fileType && value) {
        return {
          value: value || '',
          show: true
        };
      }
      if (currentColumn && currentColumn.type === CatalogType.Dictionary && !value) {
        const newValue = assign('', { show: false, value: ' ' });
        this.emptyValue.push(newValue);
        return newValue;
      }
    }

    // if (!this.showAllFields && !value && !key.includes('_en') && value !== 0) {
    if (!this.showAllFields && !value && value !== 0) {
      const newValue = assign(value, { show: false, value: ' ' });
      this.emptyValue.push(newValue);
      return newValue;
    }

    return assign(value, { show: true });
  };

  private setImageValue(columns: CatalogDynamicColumns[], itemData: CatalogItemValue | null): CatalogDynamicColumns[] {
    if (!columns) {
      return [];
    }

    return columns.map(x => {
      if (x.type === this.fileType && itemData && itemData[x.name] && itemData[x.name].value && itemData[x.name].value.trim().length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        const imageResult = this.isImageTypeFile(itemData[x.name].value);
        x.imageInfo = imageResult;
      }
      if (x.type === CatalogType.Catalog && itemData && itemData[x.name]) {
        x.catalogColumns = this.setImageValue(
          x.catalogColumns as CatalogDynamicColumns[],
          Array.isArray(itemData[x.name]) ? itemData[x.name][0] : itemData[x.name]
        );
      }
      return x;
    });
  }

  public resizeTitle($event: ResizedEvent): void {
    if (this.title) {
      this.itemHeight = `calc(100% - ${$event.newRect.height + 10 + 15 + 8 + 73}px)`;
    } else {
      this.itemHeight = `calc(100% - ${$event.newRect.height + 10 + 15 + 8}px)`;
    }
    this.cdr.detectChanges();
  }

  public showTableData(itemData: CatalogItemTableData): void {
    itemData.showTableData = !itemData.showTableData;
  }

  public showEmptyValueFields(): void {
    this.emptyValue.forEach(v => {
      v.show = !v.show;
    });
    // открываем таблицы
    this._allTableItem.forEach(t => {
      t.showTableData = true;
    });
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public openLinkCard(value: { global_id: number; value: string }, column: CatalogDynamicColumns): void {
    const modalDialog = this.dialog.open(CatalogItemCardLinkModalComponent, { backdropClass: 'closer-backdrop-transparent' });
    modalDialog.componentInstance.globalId = +value.global_id;
    modalDialog.componentInstance.catalogId = +column.catalogId;
    modalDialog.componentInstance.title = column.title;
    modalDialog.componentInstance.titleInner = value.value;
  }

  public openArrayCard(value: CatalogItemTableData, column: SelectOptionMobile): void {
    const modalDialog = this.dialog.open(CatalogItemsModalComponent, {
      backdropClass: 'closer-backdrop-transparent',
      width: '100%',
      maxWidth: '710px'
    });
    modalDialog.componentInstance.catalogId = +column.catalogId;
    modalDialog.componentInstance.arr = value;
    modalDialog.componentInstance.title = column.title;
  }

  fileDataList(fileIds?: string): FileData[] {
    return (
      fileIds
        ?.split(';')
        .map(id => this.fileNameMap.get(id))
        .filter(Boolean) || []
    ) as FileData[];
  }

  private hasEmptyFields(data: object, path: string[] = []): boolean {
    return Object.keys(data).some((key, index) => {
      const value = data[key];

      if (Array.isArray(value) && this.columnsMap.get(key) !== 'LINK') {
        path.push(key);
        if (typeof value[index] === 'object') {
          value.forEach(v => { this.hasEmptyFields(v, path); });
        }
        path.pop();
      }

      const currentColumn = this.findColumn(key, path, this.itemColumns);
      return !!value
        || (Array.isArray(value) && value.length === 0)
        || (currentColumn && currentColumn.type === CatalogType.Dictionary && !value)
        || (!this.showAllFields && !value && value !== 0 && !key.includes('_en'));
    });
  }
}
